import moment from "moment";
import { isCommissioningSlaStatus } from "../config/iotSlaConfig";

/**
 * What the asset hero offers for the IoT commissioning (IBM-1045). "Start commissioning"
 * is offered whenever the SLA is INACTIVE or EVALUATION_FAILED — also while a recent commissioning is pending,
 * so the checklist can be repeated on site without waiting 24 h:
 * - `button`: the SLA is INACTIVE / EVALUATION_FAILED and no recent commissioning is pending —
 *   the button plus the status hint
 * - `pending`: a commissioning was submitted within the last 24 h and IoT has not moved the
 *   SLA out of INACTIVE / EVALUATION_FAILED yet — the button plus the "submitted" line in place of the hint
 * - `none`: nothing (flag off, not an IoT admin, no connection, any other SLA status or unknown)
 *
 * The SLA status comes from IoT and may be unavailable (the resolver nulls the
 * connection for devices without an SLA record) — unknown reads as `none`, so
 * an IoT outage never offers the commissioning on an active device.
 */
export type IotCommissioningState = "button" | "pending" | "none";

/** A commissioning submitted less than this long ago shows the "submitted" line next to the button while IoT catches up. */
export const COMMISSIONING_PENDING_WINDOW_HOURS = 24;

export interface IotCommissioningStateInput {
    /** isSlaEnabled() */
    enabled: boolean;
    isIoTAdmin: boolean;
    hasConnection: boolean;
    /** `iotConnection.slaInformation.status`; null when unknown / not loaded. */
    slaStatus: string | null | undefined;
    /** `insert` of the newest ASSET_IOT_COMMISSIONING event of the asset; null when none. */
    lastCommissioningAt: string | null | undefined;
    /**
     * The commissioning event lookup has answered (or was not needed). While it is still
     * in flight, INACTIVE / EVALUATION_FAILED must not read as `button`: the hero would flash the "Commissioning
     * required" hint before switching to the "submitted" line. Defaults to true.
     */
    lookupSettled?: boolean;
    /** Injectable clock for tests (epoch ms). */
    now?: number;
}

export const resolveIotCommissioningState = ({
    enabled, isIoTAdmin, hasConnection, slaStatus, lastCommissioningAt, lookupSettled = true, now = Date.now(),
}: IotCommissioningStateInput): IotCommissioningState => {
    if (!enabled || !isIoTAdmin || !hasConnection) return "none";
    if (!isCommissioningSlaStatus(slaStatus)) return "none";
    if (!lookupSettled) return "none";
    if (lastCommissioningAt) {
        // Strict ISO parse: `insert` is a DateTime scalar; anything else is ignored (no moment fallback warning).
        const submitted = moment(lastCommissioningAt, moment.ISO_8601, true);
        if (submitted.isValid()) {
            const ageHours = moment(now).diff(submitted, "hours", true);
            if (ageHours >= 0 && ageHours < COMMISSIONING_PENDING_WINDOW_HOURS) return "pending";
        }
    }
    return "button";
};
